import { buttonStyle } from 'components/buttons/buttonStyle';
import { ListIcon } from 'components/icons';
import { BOTH_DESIGNS_DIALOG_800 } from 'components/modals/dialogWidths';
import { useModal } from 'components/modals/useModal';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { formatToHours } from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { FormattedMessage } from 'react-intl';
import { useGetInvoiceTotalsQuery } from 'store/api/invoices';
import { InvoiceData } from 'types/invoices';
import { t } from 'utils/getTranslations';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import { InvoiceSummaryDialog } from './InvoiceSummaryDialog';

/**
 * The first row of the Details tab (the new design's Cases tab), on a debit invoice only: its cases, their billed
 * hours as the case cards show them and the receipt's total without VAT, then Summary, which opens the invoice
 * summary. A shared-payment copy's total is the whole invoice's, as on its receipt.
 */
export function InvoiceSummaryRow({ invoice }: { invoice: InvoiceData }) {
    const { isDebit } = useInvoice(invoice);
    const { hasShareTotals: isShared } = useSharedPayment(invoice);
    const { data: totals } = useGetInvoiceTotalsQuery(invoice.id);
    const { displayDecimalScale } = useTimecardPrecision();
    const { openModal, closeModal } = useModal();
    const isNewDesign = useIsNewDesign();
    if (!isDebit) {
        return null;
    }

    const matters = invoice.invoiceMatters ?? [];
    const billedHours = matters.reduce((sum, matter) => sum + (matter.calculationLog?.fees?.billedTimeInHours ?? 0), 0);
    const net = (isShared ? invoice.preSplitTotals : totals)?.paymentCurrency.invoice.totalWithoutVat ?? 0;
    const open = () =>
        openModal(
            <InvoiceSummaryDialog invoice={invoice} isShared={isShared} onClose={closeModal} />,
            closeModal,
            undefined,
            undefined,
            BOTH_DESIGNS_DIALOG_800
        );

    return (
        // The new design's side panel toggle sits at the cases' top right edge: the row's end keeps clear of it.
        <div className="new:pe-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <div className="text-body2 text-fg-secondary new:text-muted-foreground tabular-nums">
                <FormattedMessage
                    id="invoices.invoice-summary.row"
                    values={{
                        cases: matters.length,
                        hours: formatToHours(billedHours, displayDecimalScale),
                        // Until the totals land the currency is unknown, so a zero would read as a real figure.
                        net: totals ? (
                            <span className="text-fg new:text-foreground font-medium">
                                <MoneyDisplay amount={net} currency={totals.paymentCurrency.currencyTypeCode} />
                            </span>
                        ) : (
                            <Skeleton className="inline-block h-4 w-20 align-middle" />
                        ),
                    }}
                />
            </div>
            {isNewDesign ? (
                <Button variant="outline" size="sm" onClick={open}>
                    <ListIcon data-icon="inline-start" aria-hidden />
                    {t('invoices.invoice-summary.button')}
                </Button>
            ) : (
                <Button {...buttonStyle({ variant: 'outlined', size: 'extraSmall' }, 'bg-paper gap-1')} onClick={open}>
                    <ListIcon className="size-4" aria-hidden />
                    {t('invoices.invoice-summary.button')}
                </Button>
            )}
        </div>
    );
}
